/**
 * Custom Event Bus for cross-tab communication
 * Uses BroadcastChannel API for synchronization between browser tabs/windows
 */

type EventListener = (...args: any[]) => void;

class CustomEventBus {
  private events: Record<string, EventListener[]> = {};
  private channel: BroadcastChannel | null = null;

  constructor(channelName = 'coffee-event-bus') {
    // Only initialize BroadcastChannel in browser environment
    if (typeof window !== 'undefined' && 'BroadcastChannel' in window) {
      this.channel = new BroadcastChannel(channelName);
      
      this.channel.onmessage = (event) => {
        const { name, args } = event.data;
        // Emit event locally, marking it as from broadcast to avoid infinite loop
        this.emit(name, ...args, true);
      };
    }
  }

  /**
   * Subscribe to an event
   */
  on(event: string, listener: EventListener): () => void {
    if (!this.events[event]) {
      this.events[event] = [];
    }
    this.events[event].push(listener);

    // Return unsubscribe function
    return () => this.off(event, listener);
  }

  /**
   * Unsubscribe from an event
   */
  off(event: string, listener: EventListener): void {
    if (!this.events[event]) return;
    
    this.events[event] = this.events[event].filter((l) => l !== listener);
  }

  /**
   * Emit an event (broadcasts to all tabs unless marked as from broadcast)
   */
  emit(event: string, ...args: any[]): void {
    // Check if this is already a broadcast event (has true as last arg)
    const isBroadcast = args[args.length - 1] === true;
    
    // If not from broadcast, send to other tabs
    if (!isBroadcast && this.channel) {
      this.channel.postMessage({ name: event, args });
    }

    // Emit locally
    if (!this.events[event]) return;
    
    this.events[event].forEach((listener) => {
      try {
        listener(...args);
      } catch (error) {
        console.error(`Error in event listener for "${event}":`, error);
      }
    });
  }

  /**
   * Close the broadcast channel (cleanup)
   */
  close(): void {
    if (this.channel) {
      this.channel.close();
      this.channel = null;
    }
    this.events = {};
  }
}

// Singleton instance
export const eventBus = new CustomEventBus();

// Event types for type safety
export const EventTypes = {
  LOGOUT: 'auth:logout',
  WORKSPACE_CHANGED: 'workspace:changed',
  TOKEN_REFRESHED: 'auth:token-refreshed',
} as const;
